// ===================================================================
// components/ForgeGame.tsx
// "La forge des mots" — les lettres sortent du feu, au hasard et avec
// des intruses. Le joueur les pose sur l'enclume, dans l'ordre, pour
// forger le mot demandé. Trois cœurs, un indice, et "Attiser" pour
// relancer les lettres.
//
// Écrans (maquettes Claude Design "Jogolinga - Forge Intro" et
// "Jogolinga - Forge") :
//   1. Commande du jour (1c) : le thème et les 6 mots à forger
//   2. Le forgeron explique (1b) : la règle, montrée la première fois
//   3. Le jeu, avec le décompte "Le feu chauffe" (1d) avant les lettres
//
// Le jeu est dessiné sur une scène fixe de 390 × 844 (comme la maquette)
// mise à l'échelle de l'écran.
// ===================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  FORGE_WORDS_PER_GAME, VocabEntry, forgeLetters, getForgeLetterPool, getForgeThemes,
} from '../utils/vocabExtract';
import { displayCategoryLabel } from '../utils/categoryLabels';
import { recordActivity } from '../hooks/useDailyStreak';
import { LanguageCode } from '../types/types';
import BackButton from './common/BackButton';
import GameStage, { STAGE_W, STAGE_H } from './common/GameStage';
import './ForgeGame.css';

interface ForgeGameProps {
  languageCode: LanguageCode;
  onBack: () => void;
}

// ── Constantes ────────────────────────────────────────────────────
const HEARTS = 3;
const DECOYS = 3;
const XP_PER_WORD = 10;
const XP_PER_HEART_LEFT = 5;
export const FORGE_MAX_XP = FORGE_WORDS_PER_GAME * XP_PER_WORD + HEARTS * XP_PER_HEART_LEFT;
const RULES_SEEN_KEY = 'forge-rules-seen';

const LANG_NAMES: Partial<Record<LanguageCode, string>> = {
  wf: 'Wolof', ba: 'Bambara', la: 'Lingala', ff: 'Peul', co: 'Copte', sw: 'Swahili', yo: 'Yoruba',
};

const C = {
  ink: '#2A1A12', sand: '#E4D4B0', paper: '#F0E3C6', border: '#D9A67A', clay: '#C67B4E',
  clayDark: '#8E4A26', brown: '#4A3424', muted: '#8A7358', red: '#A4553A', gold: '#D9A441',
  cream: '#FFF8EC', line: '#D9B892',
};
const SERIF = "'Young Serif', Georgia, serif";

// ── Utilitaires ───────────────────────────────────────────────────
const rnd = (a: number, b: number) => a + Math.random() * (b - a);
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
/** Sens affiché : sans les précisions entre parenthèses ("Bien (familier…)" → "Bien"). */
const cleanMeaning = (clue: string) => clue.replace(/\s*\([^)]*\)/g, '').trim() || clue;
const abs = (s: React.CSSProperties): React.CSSProperties => ({ position: 'absolute', ...s });
/** Style avec variables CSS (--dx, --dy) pour les étincelles. */
const withVars = (s: Record<string, string | number>) => s as React.CSSProperties;

function awardXp(languageCode: LanguageCode, xpGained: number) {
  if (xpGained <= 0) return;
  try {
    const xpKey = `${languageCode}-totalXP`;
    const newTotal = parseInt(localStorage.getItem(xpKey) || '0', 10) + xpGained;
    localStorage.setItem(xpKey, String(newTotal));
    window.dispatchEvent(new CustomEvent('xpUpdated', { detail: { newTotal, gained: xpGained, source: 'forge' } }));
  } catch { /* ignore */ }
  recordActivity();
}

// ── Icônes ────────────────────────────────────────────────────────
const CloseIcon = ({ color }: { color: string }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round"><path d="M5 5L19 19M19 5L5 19" /></svg>
);
const HeartIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={C.red}><path d="M12 21C5 15.5 2 12.3 2 8.5A4.8 4.8 0 0 1 12 6.2A4.8 4.8 0 0 1 22 8.5C22 12.3 19 15.5 12 21Z" /></svg>
);
const FlameIcon = ({ color = C.clay, size = 24 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round"><path d="M12 22C8 22 5.5 19.4 5.5 16C5.5 12 9 10 9.5 5.5C12 7 13 9.5 12.6 12C14 11.2 14.8 9.8 15 8.5C17.2 10.5 18.5 13 18.5 16C18.5 19.4 16 22 12 22Z" /></svg>
);
const SkipIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.clay} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 5L13 12L5 19Z M13 5L21 12L13 19Z" /></svg>
);
const BulbIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.clay} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18H15M10 21H14M12 3A6 6 0 0 0 8.5 13.9C9.3 14.5 9.5 15.2 9.5 16H14.5C14.5 15.2 14.7 14.5 15.5 13.9A6 6 0 0 0 12 3Z" /></svg>
);

/** « ‹ Retour » commun aux jeux de Pratiquer, en haut à gauche de la scène. */
const StageBack: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <BackButton onClick={onClick} style={abs({ left: 16, top: 24 })} />
);

const PrimaryButton: React.FC<{ onClick: () => void; children: React.ReactNode; style?: React.CSSProperties }> = ({ onClick, children, style }) => (
  <button type="button" className="forge-primary" onClick={onClick} style={style}>{children}</button>
);

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: '.2em', color: C.clay, whiteSpace: 'nowrap' }}>◆ {children} ◆</span>
);

// ── Décor : enclume, feu, forgeron ────────────────────────────────
const Anvil: React.FC<{ top: number; left?: number; width?: number; waist?: number; base?: number }> = ({
  top, left = 30, width = 288, waist = width * 0.3, base = width * 0.5,
}) => {
  const mid = left + width / 2;
  return (
    <>
      <div style={abs({ left: left - 24, top, width: 26, height: 14, background: C.ink, clipPath: 'polygon(100% 0,100% 100%,0 0)' })} />
      <div style={abs({ left, top, width, height: 20, borderRadius: '4px 4px 2px 2px', background: C.ink })} />
      <div style={abs({ left: mid - waist / 2, top: top + 20, width: waist, height: 20, background: '#3A2A1E' })} />
      <div style={abs({ left: mid - base / 2, top: top + 40, width: base, height: 12, borderRadius: 3, background: C.ink })} />
    </>
  );
};

const FIRE = (
  <div style={abs({ inset: 0 })}>
    <div style={abs({ left: -30, right: -30, bottom: -50, height: 170, background: 'radial-gradient(ellipse at 50% 100%, #FFD27A 0%, #F08A2C 30%, #B23A12 55%, rgba(20,12,7,0) 75%)', animation: 'jgGlow 1.6s ease-in-out infinite alternate' })} />
    {[18, 44, 66, 92, 112].map((l, i) => (
      <div key={i} style={abs({ left: l, bottom: 16, width: 34, height: 58 + (i % 2) * 18, borderRadius: '50% 50% 45% 45% / 70% 70% 30% 30%', background: 'radial-gradient(ellipse at 50% 80%, #FFE6A8, #F59A3A 45%, rgba(200,70,20,0) 72%)', transformOrigin: '50% 100%', animation: `jgFlick ${(0.8 + i * 0.17).toFixed(2)}s ease-in-out ${(i * 0.13).toFixed(2)}s infinite alternate` })} />
    ))}
    <div style={abs({ left: 0, right: 0, bottom: 0, height: 24, background: 'radial-gradient(circle at 20% 40%, #E0602A 0 5px, transparent 6px), radial-gradient(circle at 48% 60%, #F08A2C 0 6px, transparent 7px), radial-gradient(circle at 76% 35%, #C9461C 0 5px, transparent 6px), #2A120A' })} />
  </div>
);

/** Le four : arche, frise de triangles et gueule de feu. */
const Furnace: React.FC = () => (
  <>
    <div style={abs({ left: 75, top: 636, width: 240, height: 208, borderRadius: '120px 120px 0 0', background: '#3A2A1E' })} />
    <svg width="208" height="12" viewBox="0 0 208 12" style={abs({ left: 91, top: 672 })}>
      <defs><pattern id="jgTri" width="16" height="12" patternUnits="userSpaceOnUse"><path d="M0 12L8 2L16 12Z" fill={C.clay} /></pattern></defs>
      <rect width="208" height="12" fill="url(#jgTri)" />
    </svg>
    <div style={abs({ left: 120, top: 690, width: 150, height: 154, borderRadius: '75px 75px 0 0', background: '#140C07', overflow: 'hidden' })}>{FIRE}</div>
  </>
);

const HIT_DOTS = Array.from({ length: 9 }, (_, i) => ({ dx: -40 + i * 9 + rnd(-4, 4), dy: rnd(-46, -14), s: rnd(3, 5) }));

/** Le forgeron. animated=false : pose fixe (écran des règles). */
function Smith({ win = false, animated = true }: { win?: boolean; animated?: boolean }) {
  const arm = !animated ? 'none' : win ? 'jgSmithWin .5s ease-in 3' : 'jgSmith 1.6s ease-in-out infinite';
  const hit = win ? 'jgHitWin .5s ease-out 3' : 'jgHit 1.6s ease-out infinite';
  return (
    <svg width="78" height="132" viewBox="0 0 78 132" style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
      <g style={{ transformBox: 'view-box', transformOrigin: '44px 128px', animation: animated && !win ? 'jgLean 1.6s ease-in-out infinite' : 'none' } as React.CSSProperties}>
        <rect x="34" y="94" width="9" height="32" rx="3" fill={C.ink} />
        <rect x="47" y="94" width="9" height="32" rx="3" fill={C.ink} />
        <ellipse cx="36" cy="128" rx="8" ry="3.5" fill={C.ink} /><ellipse cx="49" cy="128" rx="8" ry="3.5" fill={C.ink} />
        <path d="M34 30Q44 25 54 30L58 82L30 82Z" fill={C.ink} />
        <path d="M33 46H55L60 98H28Z" fill={C.red} />
        <path d="M31 90L35 86L39 90L43 86L47 90L51 86L55 90L58 87" fill="none" stroke={C.paper} strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M32 58H56" stroke={C.paper} strokeWidth="1.2" strokeDasharray="2 3" />
        <path d="M52 36L58 56L54 72" fill="none" stroke={C.ink} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="44" cy="18" r="9" fill={C.ink} />
        <path d="M35 15Q44 3 53 15Z" fill={C.clay} />
        <g style={{ transformBox: 'view-box', transformOrigin: '40px 36px', animation: arm, transform: animated ? undefined : 'rotate(80deg)' } as React.CSSProperties}>
          <path d="M40 36L28 50L20 58" fill="none" stroke={C.ink} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M21 57L10 68" stroke="#8A5A34" strokeWidth="4" strokeLinecap="round" />
          <rect x="-2" y="63" width="20" height="10" rx="2" fill={C.brown} stroke={C.ink} strokeWidth="1.5" transform="rotate(38 8 68)" />
        </g>
      </g>
      {animated && HIT_DOTS.map((d, i) => (
        <circle key={i} cx={8} cy={76} r={d.s / 2} fill="#FFC062" style={withVars({ opacity: 0, '--dx': `${d.dx}px`, '--dy': `${d.dy}px`, animation: hit })} />
      ))}
    </svg>
  );
}

// ── Données d'une partie ──────────────────────────────────────────
interface ForgeWord {
  letters: string[];
  word: string;
  meaning: string;
}
interface ForgeOrder {
  category: string;
  words: ForgeWord[];
}

// ===================================================================
// Écran 1 : Commande du jour (1c)
// ===================================================================
const OrderScreen: React.FC<{
  order: ForgeOrder; langName: string; t: TFunction;
  onClose: () => void; onEnter: () => void; onRules: () => void;
}> = ({ order, langName, t, onClose, onEnter, onRules }) => (
  <div className="forge-screen" style={{ background: C.sand }}>
    <StageBack onClick={onClose} />
    <div style={abs({ left: 24, right: 24, top: 92, display: 'flex', flexDirection: 'column', gap: 6 })}>
      <Eyebrow>{t('forge.eyebrow', 'LA FORGE DES MOTS')}</Eyebrow>
      <span style={{ fontFamily: SERIF, fontSize: 34, lineHeight: 1.1 }}>
        {order.category ? displayCategoryLabel(order.category, t) : t('forge.mixedTheme', 'Mélange')}
      </span>
    </div>
    <div style={abs({ left: 24, right: 24, top: 190, borderRadius: 20, background: C.paper, border: `1.5px solid ${C.border}`, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 })}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: '.12em', color: C.muted }}>{t('forge.toForge', 'À FORGER')}</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: C.brown }}>0 / {order.words.length}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {order.words.map((w, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: C.sand, minWidth: 0 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #B89A74', boxSizing: 'border-box', flex: 'none' }} />
            <span style={{ fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.meaning}</span>
          </div>
        ))}
      </div>
      <div style={{ height: 1, background: C.line }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ fontSize: 12, color: C.muted }}>{t('forge.language', 'Langue')}</span><span style={{ fontWeight: 700, fontSize: 16 }}>{langName}</span></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ fontSize: 12, color: C.muted }}>{t('forge.duration', 'Durée')}</span><span style={{ fontWeight: 700, fontSize: 16 }}>~4 min</span></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ fontSize: 12, color: C.muted }}>{t('forge.hearts', 'Cœurs')}</span><span style={{ fontWeight: 700, fontSize: 16, color: C.red }}>{HEARTS}</span></div>
      </div>
    </div>
    <div style={abs({ left: 24, right: 24, top: 560, display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 16, background: C.ink, color: C.paper })}>
      <span style={{ width: 44, height: 44, borderRadius: '50%', background: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.ink} strokeWidth="1.8"><path d="M12 2L22 12L12 22L2 12Z" /><path d="M12 7L17 12L12 17L7 12Z" /></svg>
      </span>
      <span style={{ fontSize: 15, lineHeight: 1.4 }}>
        {t('forge.reward', 'Forge les {{count}} mots pour gagner', { count: order.words.length })}{' '}
        <b style={{ color: C.gold }}>{t('forge.rewardAmount', 'jusqu’à {{xp}} XP', { xp: FORGE_MAX_XP })}</b>
      </span>
    </div>
    <PrimaryButton onClick={onEnter} style={abs({ left: 24, right: 24, bottom: 52, width: 'auto' })}>{t('forge.enter', 'Entrer dans la forge')}</PrimaryButton>
    <button type="button" className="forge-link" onClick={onRules} style={abs({ left: 0, right: 0, bottom: 14 })}>
      {t('forge.howToPlay', 'Comment jouer ?')}
    </button>
  </div>
);

// ===================================================================
// Écran 2 : Le forgeron explique (1b)
// ===================================================================
const RulesScreen: React.FC<{ t: TFunction; onClose: () => void; onDone: () => void }> = ({ t, onClose, onDone }) => {
  const tile = (bg: string, fg: string, extra: React.CSSProperties = {}): React.CSSProperties => ({
    width: 40, height: 44, borderRadius: 9, background: bg, color: fg, fontFamily: SERIF, fontSize: 22,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', boxSizing: 'border-box', ...extra,
  });
  return (
    <div className="forge-screen" style={{ background: C.sand }}>
      <StageBack onClick={onClose} />
      <div style={abs({ left: 24, right: 24, top: 96, padding: '18px 20px', borderRadius: 18, background: C.paper, border: `1.5px solid ${C.border}`, fontFamily: SERIF, fontSize: 21, lineHeight: 1.35 })}>
        {t('forge.rules.bubble', 'Je forge les lettres. À toi d’assembler les mots avant qu’elles refroidissent.')}
      </div>
      <div style={abs({ left: 250, top: 208, width: 22, height: 18, background: C.paper, borderRight: `1.5px solid ${C.border}`, borderBottom: `1.5px solid ${C.border}`, transform: 'skewX(-30deg)' })} />
      <div style={abs({ left: 200, top: 232, width: 156, height: 264 })}>
        <div style={{ position: 'absolute', inset: 0, transform: 'scale(2)', transformOrigin: '0 0', width: 78, height: 132 }}>
          <Smith animated={false} />
        </div>
      </div>
      <Anvil top={420} left={54} width={170} waist={64} base={112} />
      <div style={abs({ left: 24, right: 24, top: 520, display: 'flex', flexDirection: 'column', gap: 12 })}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={tile('#F6B04A', '#6B2208', { boxShadow: '0 0 12px 3px rgba(255,140,40,.55)' })}>n</span>
          <span style={{ fontSize: 16, lineHeight: 1.35 }}><b>{t('forge.rules.r1Bold', 'Les lettres sortent du feu')}</b>{t('forge.rules.r1', ', au hasard, avec des intruses.')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={tile(C.brown, C.paper, { boxShadow: `0 3px 0 ${C.ink}` })}>d</span>
          <span style={{ fontSize: 16, lineHeight: 1.35 }}><b>{t('forge.rules.r2Bold', 'Touche-les')}</b>{t('forge.rules.r2', ' pour les poser sur l’enclume, dans l’ordre.')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={tile(C.paper, C.red, { border: `1.5px solid ${C.border}` })}><HeartIcon size={20} /></span>
          <span style={{ fontSize: 16, lineHeight: 1.35 }}><b>{t('forge.rules.r3Bold', 'Trois cœurs.')}</b>{t('forge.rules.r3', ' Une erreur et le feu baisse.')}</span>
        </div>
      </div>
      <PrimaryButton onClick={onDone} style={abs({ left: 24, right: 24, bottom: 40, width: 'auto' })}>{t('forge.rules.cta', 'J’ai compris, on forge')}</PrimaryButton>
    </div>
  );
};

// ===================================================================
// Écran 3 : le jeu
// ===================================================================
interface Tile {
  id: number;
  ch: string;
  rx: number; ry: number; rr: number; // position au sol
  out: boolean; // sortie du feu
  hot: boolean; // encore incandescente
  gold?: boolean;
}
interface Burst { id: number; x: number; y: number; dots: { dx: number; dy: number; s: number }[] }
type Status = 'ready' | 'play' | 'wrong' | 'win' | 'skipped' | 'over' | 'done';

interface BoardProps {
  words: ForgeWord[];
  decoyPool: string[];
  langName: string;
  langLower: string;
  t: TFunction;
  onExit: () => void;
  onNewOrder: () => void;
  onXp: (xp: number) => void;
}
interface BoardState {
  idx: number;
  tiles: Tile[];
  slots: (number | null)[];
  hearts: number;
  status: Status;
  bursts: Burst[];
  hammer: number;
  countdown: number;
  lastXp: number;
}

const MOUTH = { x: 169, y: 742 };
const HOT = { bg: '#F6B04A', fg: '#6B2208', sh: '0 0 22px 6px rgba(255,140,40,.75), inset 0 -3px 0 rgba(160,60,10,.45)' };
const COOL = { bg: C.brown, fg: C.paper, sh: `0 4px 0 ${C.ink}, 0 8px 14px rgba(42,26,18,.25)` };
const GOLD = { bg: C.gold, fg: C.ink, sh: '0 0 18px 4px rgba(217,164,65,.8), 0 4px 0 #9A6A1E' };
const TILE_W = 52;
const TILE_H = 58;
const SLOT_BOTTOM = 304; // juste au-dessus de l'enclume (306)
const ANVIL_CENTER = 174; // enclume : x 30 → 318
const ANVIL_ROOM = 284;

/** 15 emplacements au sol (5 × 3), mélangés, légèrement de travers. */
const cells = () => shuffle(Array.from({ length: 15 }, (_, i) => i)).map(c => ({
  rx: 25 + 34 + (c % 5) * 68 - 26 + rnd(-8, 8),
  ry: 404 + 35 + Math.floor(c / 5) * 70 - 29 + rnd(-7, 7),
  rr: rnd(-14, 14),
}));

/**
 * Cases de l'enclume, centrées sur l'enclume (pas sur l'écran, sinon elles
 * passent sous le forgeron). Plus petites quand le mot est long ; les lettres
 * posées rétrécissent à la même échelle.
 */
function slotLayout(n: number) {
  const gap = n <= 4 ? 8 : 5;
  const w = Math.min(54, (ANVIL_ROOM - (n - 1) * gap) / n);
  const h = (w * 60) / 54;
  const x0 = ANVIL_CENTER - (n * w + (n - 1) * gap) / 2;
  return {
    w, h, top: SLOT_BOTTOM - h,
    tileScale: Math.min(1, (w - 2) / TILE_W),
    xs: Array.from({ length: n }, (_, k) => x0 + k * (w + gap)),
  };
}

let UID = 0;

class ForgeBoard extends React.Component<BoardProps, BoardState> {
  state: BoardState = {
    idx: 0, tiles: [], slots: Array(this.props.words[0].letters.length).fill(null),
    hearts: HEARTS, status: 'ready', bursts: [], hammer: 0, countdown: 3, lastXp: 0,
  };
  timers: ReturnType<typeof setTimeout>[] = [];
  forged = 0;   // mots réussis dans cette partie
  awarded = 0;  // mots déjà payés en XP
  winKey = -1;
  winEl: React.ReactNode = null;

  later(fn: () => void, ms: number) { this.timers.push(setTimeout(fn, ms)); }

  componentDidMount() {
    this.later(() => this.setState({ countdown: 2 }), 800);
    this.later(() => this.setState({ countdown: 1 }), 1600);
    this.later(() => { this.setState({ countdown: 0 }); this.forge(0); }, 2400);
  }

  componentWillUnmount() {
    this.timers.forEach(clearTimeout);
    this.payWords(); // partie quittée en cours : les mots forgés comptent
  }

  /** Paie les mots forgés pas encore payés (+ un bonus éventuel). */
  payWords(bonus = 0): number {
    const xp = (this.forged - this.awarded) * XP_PER_WORD + bonus;
    this.awarded = this.forged;
    if (xp > 0) this.props.onXp(xp);
    return xp;
  }

  burst(x: number, y: number, n = 10, up = true) {
    const id = ++UID;
    const dots = Array.from({ length: n }, () => ({ dx: rnd(-60, 60), dy: up ? rnd(-110, -30) : rnd(-60, 20), s: rnd(3, 6) }));
    this.setState(s => ({ bursts: [...s.bursts, { id, x, y, dots }] }));
    this.later(() => this.setState(s => ({ bursts: s.bursts.filter(b => b.id !== id) })), 900);
  }

  /** Fait sortir les lettres du feu une à une ; elles refroidissent ensuite. */
  emerge(ids: number[]) {
    ids.forEach((id, i) => {
      this.later(() => {
        this.setState(s => ({ tiles: s.tiles.map(t => (t.id === id ? { ...t, out: true } : t)) }));
        this.burst(195, 738, 8);
      }, 200 + i * 230);
      this.later(() => this.setState(s => ({
        tiles: s.tiles.map(t => (t.id === id && !s.slots.includes(id) ? { ...t, hot: false } : t)),
      })), 1400 + i * 230);
    });
  }

  forge(idx: number) {
    const word = this.props.words[idx].letters;
    const pool = this.props.decoyPool.length ? this.props.decoyPool : word;
    const decoys = Array.from({ length: DECOYS }, () => pool[Math.floor(Math.random() * pool.length)]);
    const letters = shuffle([...word, ...decoys]);
    const pos = cells();
    const tiles: Tile[] = letters.map((ch, i) => ({ id: ++UID, ch, ...pos[i], out: false, hot: true }));
    this.setState({ idx, tiles, slots: Array(word.length).fill(null), status: 'play' });
    this.emerge(tiles.map(t => t.id));
  }

  /** "Attiser" : les lettres libres retournent au feu et ressortent ailleurs. */
  stoke = () => {
    const { status, tiles, slots } = this.state;
    if (status !== 'play') return;
    const free = tiles.filter(t => !slots.includes(t.id) && t.out).map(t => t.id);
    if (!free.length) return;
    this.setState(s => ({ tiles: s.tiles.map(t => (free.includes(t.id) ? { ...t, out: false, hot: true } : t)) }));
    this.later(() => {
      const pos = cells();
      this.setState(s => ({
        tiles: s.tiles.map(t => { const k = free.indexOf(t.id); return k < 0 ? t : { ...t, ...pos[k] }; }),
      }));
      this.emerge(shuffle(free));
    }, 500);
  };

  place(slots: (number | null)[]) {
    this.setState({ slots });
    if (slots.every(x => x !== null)) this.later(() => this.check(), 650);
  }

  tap(id: number) {
    const { status, slots, tiles } = this.state;
    const tile = tiles.find(x => x.id === id);
    if (status !== 'play' || !tile || !tile.out) return;
    if (slots.every(x => x !== null)) return; // vérification en cours
    const next = [...slots];
    const k = slots.indexOf(id);
    if (k >= 0) next[k] = null;
    else {
      const f = next.indexOf(null);
      if (f < 0) return;
      next[f] = id;
    }
    this.place(next);
  }

  /** Indice : pose la bonne lettre à la première case fausse. */
  hint = () => {
    const { status, slots, tiles, idx } = this.state;
    if (status !== 'play' || slots.every(x => x !== null)) return;
    const word = this.props.words[idx].letters;
    const chOf = (id: number | null) => tiles.find(t => t.id === id)?.ch;
    const i = word.findIndex((c, j) => chOf(slots[j]) !== c);
    if (i < 0) return;
    const next = [...slots];
    next[i] = null;
    const cand = tiles.find(t => t.out && t.ch === word[i] && !next.includes(t.id));
    if (!cand) return;
    const j = next.indexOf(cand.id);
    if (j >= 0) next[j] = null;
    next[i] = cand.id;
    this.place(next);
  };

  /** Passer : révèle le mot, sans perdre de cœur ni gagner d'XP. */
  skip = () => {
    const { status, slots } = this.state;
    if (status !== 'play' || slots.every(x => x !== null)) return;
    this.setState({ status: 'skipped' });
  };

  check() {
    const { slots, tiles, idx, hearts } = this.state;
    const guess = slots.map(id => tiles.find(t => t.id === id)?.ch).join('');
    if (guess === this.props.words[idx].letters.join('')) {
      this.forged += 1;
      this.setState(s => ({
        status: 'win', hammer: s.hammer + 1,
        tiles: s.tiles.map(t => (s.slots.includes(t.id) ? { ...t, gold: true } : t)),
      }));
      this.later(() => this.burst(195, 300, 16, false), 260);
    } else {
      const left = hearts - 1;
      this.setState({ status: 'wrong', hearts: left });
      this.later(() => {
        if (left <= 0) this.payWords();
        this.setState(s => ({ slots: s.slots.map(() => null), status: left <= 0 ? 'over' : 'play' }));
      }, 700);
    }
  }

  next = () => {
    const { idx, hearts } = this.state;
    if (idx + 1 >= this.props.words.length) {
      const xp = this.payWords(hearts * XP_PER_HEART_LEFT);
      this.setState({ status: 'done', lastXp: xp });
      return;
    }
    this.setState(s => ({ tiles: s.tiles.map(t => ({ ...t, out: false })) }));
    this.later(() => this.forge(idx + 1), 350);
  };

  /** "Rallumer la forge" : trois cœurs, on reprend au mot raté. */
  relight = () => {
    this.setState({ hearts: HEARTS });
    this.forge(this.state.idx);
  };

  renderSheet() {
    const { status, idx, hearts, lastXp } = this.state;
    const { t, words } = this.props;
    const w = words[idx];
    const sheets: Partial<Record<Status, { title: string; color: string; text: string; cta: string; action: () => void; secondary?: boolean }>> = {
      win: {
        title: t('forge.win.title', 'Bien forgé !'), color: C.ink,
        text: t('forge.win.text', '{{word}} veut dire « {{meaning}} ».', { word: w.word, meaning: w.meaning }),
        cta: idx + 1 >= words.length ? t('forge.win.finish', 'Terminer') : t('forge.win.next', 'Continuer'),
        action: this.next,
      },
      skipped: {
        title: t('forge.skipped.title', 'Mot passé'), color: C.ink,
        text: t('forge.skipped.text', 'Le mot était {{word}} (« {{meaning}} »).', { word: w.word, meaning: w.meaning }),
        cta: idx + 1 >= words.length ? t('forge.win.finish', 'Terminer') : t('forge.win.next', 'Continuer'),
        action: this.next,
      },
      done: {
        title: t('forge.done.title', 'Toute la forge est à toi'), color: C.ink,
        text: t('forge.done.text', '{{count}} mots forgés avec {{hearts}} cœur(s) restant(s).', { count: this.forged, hearts })
          + (lastXp > 0 ? ` +${lastXp} XP` : ''),
        cta: t('forge.done.cta', 'Nouvelle commande'), action: this.props.onNewOrder, secondary: true,
      },
      over: {
        title: t('forge.over.title', 'Le feu s’est éteint'), color: C.red,
        text: t('forge.over.text', 'Le mot était {{word}}. Rallume la forge pour réessayer.', { word: w.word }),
        cta: t('forge.over.cta', 'Rallumer la forge'), action: this.relight, secondary: true,
      },
    };
    const sh = sheets[status];
    if (!sh) return null;
    return (
      <div className="forge-sheet" role="dialog" aria-live="polite">
        <span style={{ fontFamily: SERIF, fontSize: 28, color: sh.color }}>{sh.title}</span>
        <span style={{ fontSize: 16, lineHeight: 1.45, color: C.brown }}>{sh.text}</span>
        <PrimaryButton onClick={sh.action} style={{ marginTop: 8 }}>{sh.cta}</PrimaryButton>
        {sh.secondary && (
          <button type="button" className="forge-link" onClick={this.props.onExit}>{t('forge.backToGames', 'Retour aux mini-jeux')}</button>
        )}
      </div>
    );
  }

  render() {
    const { idx, tiles, slots, hearts, status, bursts, hammer, countdown } = this.state;
    const { words, t, langName, langLower, onExit } = this.props;
    const w = words[idx];
    const layout = slotLayout(slots.length);
    const progress = ((idx + (status === 'win' || status === 'skipped' || status === 'done' ? 1 : 0)) / words.length) * 100;

    if (status === 'win' && this.winKey !== hammer) {
      this.winKey = hammer;
      this.winEl = <Smith key={`win${hammer}`} win />;
    }

    return (
      <div className="forge-screen" style={{ background: C.sand }}>
        {/* En-tête : fermer, progression, cœurs */}
        <div style={abs({ left: 16, right: 16, top: 22, display: 'flex', alignItems: 'center', gap: 14 })}>
          <button type="button" className="forge-icon-btn" onClick={onExit} aria-label={t('forge.close', 'Fermer')}
            style={{ width: 44, height: 44, borderRadius: 12, background: C.paper, flex: 'none' }}>
            <CloseIcon color={C.ink} />
          </button>
          <div style={{ flex: 1, height: 12, borderRadius: 6, background: '#D4BE94', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 6, background: C.clay, transition: 'width .6s ease', width: `${progress}%` }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: 17, color: C.red }} aria-label={t('forge.heartsLeft', '{{count}} cœur(s)', { count: hearts })}>
            <HeartIcon /><span>{hearts}</span>
          </div>
        </div>
        <div style={abs({ left: 24, right: 24, top: 88, display: 'flex', alignItems: 'center', gap: 12 })}>
          <span style={{ flex: 1, height: 1, background: C.line }} />
          <Eyebrow>{t('forge.eyebrow', 'LA FORGE DES MOTS')}</Eyebrow>
          <span style={{ flex: 1, height: 1, background: C.line }} />
        </div>
        <div style={abs({ left: 24, right: 24, top: 116, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' })}>
          <span style={{ fontSize: 15, color: C.brown }}>{t('forge.prompt', 'Forge ce mot en {{langLower}}', { lang: langName, langLower })}</span>
          <span style={{ fontFamily: SERIF, fontSize: w.meaning.length > 24 ? 24 : w.meaning.length > 16 ? 28 : 36, lineHeight: 1.15 }}>« {w.meaning} »</span>
        </div>

        <div style={abs({ inset: 0, pointerEvents: 'none', animation: status === 'wrong' ? 'jgShake .45s ease-in-out' : 'none' })}>
          <Anvil top={306} />
          {layout.xs.map((x, k) => (
            <div key={k} style={abs({ top: layout.top, left: x, width: layout.w, height: layout.h, boxSizing: 'border-box', borderRadius: 10, border: '2px dashed #9A7A58', background: 'rgba(42,26,18,.06)' })} />
          ))}
          <span style={abs({ left: 0, right: 0, top: 378, textAlign: 'center', fontSize: 13, color: C.muted })}>
            {t('forge.tapHint', 'Touche les lettres sorties de la forge')}
          </span>
          <Furnace />
          <div style={abs({ left: 0, top: 0, width: STAGE_W, height: STAGE_H, pointerEvents: 'none' })}>
            {bursts.map(b => (
              <div key={b.id} style={abs({ left: b.x, top: b.y })}>
                {b.dots.map((d, i) => (
                  <span key={i} style={withVars({ position: 'absolute', width: d.s, height: d.s, borderRadius: '50%', background: '#FFC062', boxShadow: '0 0 6px #FF9A2E', '--dx': `${d.dx}px`, '--dy': `${d.dy}px`, animation: 'jgSpark .8s ease-out forwards' })} />
                ))}
              </div>
            ))}
          </div>
          {tiles.map(tile => {
            const k = slots.indexOf(tile.id);
            const look = tile.gold && k >= 0 ? GOLD : tile.hot ? HOT : COOL;
            let x: number, y: number, tf: string, op = 1, z = 2;
            if (!tile.out) { x = MOUTH.x; y = MOUTH.y; tf = 'scale(.3)'; op = 0; }
            else if (k >= 0) { x = layout.xs[k] + (layout.w - TILE_W) / 2; y = layout.top + layout.h / 2 - TILE_H / 2; tf = `rotate(0deg) scale(${layout.tileScale.toFixed(3)})`; z = 5; }
            else { x = tile.rx; y = tile.ry; tf = `rotate(${tile.rr.toFixed(1)}deg) scale(1)`; }
            return (
              <button type="button" key={tile.id} className="forge-tile" onClick={() => this.tap(tile.id)}
                tabIndex={tile.out ? 0 : -1} aria-label={tile.ch}
                style={{ left: x, top: y, transform: tf, opacity: op, backgroundColor: look.bg, color: look.fg, boxShadow: look.sh, zIndex: z, pointerEvents: tile.out ? 'auto' : 'none' }}>
                {tile.ch}
              </button>
            );
          })}
          <div style={abs({ left: 312, top: 228, width: 78, height: 132, pointerEvents: 'none' })}>
            {status === 'win' ? this.winEl : <Smith />}
          </div>
        </div>

        {/* Indice, Passer et Attiser */}
        <button type="button" className="forge-round" onClick={this.skip} style={abs({ right: 12, top: 672 })}>
          <span className="forge-round__icon"><SkipIcon /></span>
          <span className="forge-round__label">{t('forge.skip', 'Passer')}</span>
        </button>
        <button type="button" className="forge-round" onClick={this.hint} style={abs({ left: 12, top: 764 })}>
          <span className="forge-round__icon"><BulbIcon /></span>
          <span className="forge-round__label">{t('forge.hint', 'Indice')}</span>
        </button>
        <button type="button" className="forge-round" onClick={this.stoke} style={abs({ right: 12, top: 764 })}>
          <span className="forge-round__icon"><FlameIcon /></span>
          <span className="forge-round__label">{t('forge.stoke', 'Attiser')}</span>
        </button>

        {/* Décompte avant les lettres (1d) */}
        {status === 'ready' && countdown > 0 && (
          <>
            <div style={abs({ inset: 0, background: 'rgba(31,20,13,.72)', zIndex: 15 })} />
            <div style={abs({ left: 0, right: 0, top: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22, zIndex: 16 })}>
              <div key={countdown} className="forge-ember">
                <span style={{ fontFamily: SERIF, fontSize: 96, lineHeight: 1, color: C.ink }}>{countdown}</span>
              </div>
              <span style={{ fontFamily: SERIF, fontSize: 26, color: C.paper }}>{t('forge.countdown.title', 'Le feu chauffe…')}</span>
              <span style={{ fontSize: 15, color: '#C9AD7A', textAlign: 'center', maxWidth: 260, lineHeight: 1.45 }}>
                {t('forge.countdown.text', 'Les lettres sortent dans un instant. Sois prêt à les attraper.')}
              </span>
            </div>
          </>
        )}

        {this.renderSheet()}
      </div>
    );
  }
}

// ===================================================================
// Composant principal
// ===================================================================
const ForgeGame: React.FC<ForgeGameProps> = ({ languageCode, onBack }) => {
  const { t, i18n } = useTranslation();

  const langName = LANG_NAMES[languageCode] ?? languageCode.toUpperCase();
  const themes = useMemo(() => getForgeThemes(languageCode), [languageCode]);
  const decoyPool = useMemo(() => getForgeLetterPool(languageCode), [languageCode]);

  const makeOrder = useCallback((): ForgeOrder | null => {
    if (themes.length === 0) return null;
    const theme = themes[Math.floor(Math.random() * themes.length)];
    const pick = shuffle(theme.entries).slice(0, FORGE_WORDS_PER_GAME);
    const english = i18n.language?.startsWith('en');
    return {
      category: theme.category,
      words: pick.map((e: VocabEntry) => ({
        letters: forgeLetters(e.word),
        word: e.word.normalize('NFC').toLocaleLowerCase(),
        meaning: cleanMeaning((english && e.clueEn) || e.clue),
      })),
    };
  }, [themes, i18n.language]);

  const [order, setOrder] = useState<ForgeOrder | null>(() => makeOrder());
  const [screen, setScreen] = useState<'order' | 'rules' | 'game'>('order');
  const [rulesThenPlay, setRulesThenPlay] = useState(false);
  const [gameKey, setGameKey] = useState(0);

  useEffect(() => { setOrder(makeOrder()); setScreen('order'); }, [makeOrder]);

  const rulesSeen = () => { try { return localStorage.getItem(RULES_SEEN_KEY) === '1'; } catch { return false; } };
  const markRulesSeen = () => { try { localStorage.setItem(RULES_SEEN_KEY, '1'); } catch { /* ignore */ } };

  const startGame = () => { setGameKey(k => k + 1); setScreen('game'); };
  const enter = () => {
    if (rulesSeen()) startGame();
    else { setRulesThenPlay(true); setScreen('rules'); }
  };
  const rulesDone = () => {
    markRulesSeen();
    if (rulesThenPlay) { setRulesThenPlay(false); startGame(); } else setScreen('order');
  };
  const newOrder = () => { setOrder(makeOrder()); setScreen('order'); };
  const onXp = useCallback((xp: number) => awardXp(languageCode, xp), [languageCode]);

  let content: React.ReactNode;
  if (!order) {
    content = (
      <div className="forge-screen" style={{ background: C.sand }}>
        <StageBack onClick={onBack} />
        <div style={abs({ left: 32, right: 32, top: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center' })}>
          <Eyebrow>{t('forge.eyebrow', 'LA FORGE DES MOTS')}</Eyebrow>
          <span style={{ fontFamily: SERIF, fontSize: 28 }}>{t('forge.comingSoon', 'Bientôt disponible')}</span>
          <span style={{ fontSize: 16, lineHeight: 1.5, color: C.brown }}>{t('forge.comingSoonText', 'La forge des mots arrive prochainement pour cette langue.')}</span>
        </div>
      </div>
    );
  } else if (screen === 'rules') {
    content = <RulesScreen t={t} onClose={() => { setRulesThenPlay(false); setScreen('order'); }} onDone={rulesDone} />;
  } else if (screen === 'game') {
    content = (
      <ForgeBoard key={gameKey} words={order.words} decoyPool={decoyPool} langName={langName}
        langLower={langName.toLocaleLowerCase()} t={t} onExit={onBack} onNewOrder={newOrder} onXp={onXp} />
    );
  } else {
    content = (
      <OrderScreen order={order} langName={langName} t={t} onClose={onBack} onEnter={enter}
        onRules={() => { setRulesThenPlay(false); setScreen('rules'); }} />
    );
  }

  // Rendu dans la page (comme les autres jeux de Pratiquer), pas en fenêtre
  // par-dessus : la scène occupe la hauteur restante sous l'en-tête.
  return <GameStage className="forge-root">{content}</GameStage>;
};

export default ForgeGame;
